import { ArrowBack, ContactSupport, Email, Language, LocationOn, Phone, Schedule } from '@mui/icons-material';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import Content from '@src/App/Components/Core/Layouts/LayoutElements/Content/Content';
import ContentHeader from '@src/App/Components/Core/Layouts/LayoutElements/ContentHeader/ContentHeader';
import { isLoggedIn } from '@store/reducer/clientReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

function Kontakt()
{
    const theme = useTheme();
    const secret = useSelector((state: AppState) => state.client.secret);
    const loggedIn = isLoggedIn(secret);

    return (
        <Content contentHeader={<ContentHeader title="Kontakt" />}>
            <Container>
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        p: 4,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        },
                    }}
                >
                    <Typography
                        component="button"
                        onClick={() => window.history.back()}
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            mb: 2,
                            display: 'block',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            textAlign: 'left',
                            '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        ← Zurück
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                            }}
                        >
                            <ContactSupport />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                Kontaktieren Sie uns
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Haben Sie Fragen zu Talentillion oder benötigen Sie Unterstützung? Wir sind gerne für
                                Sie da.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}
                                        >
                                            E-Mail
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            inbox@talentillion.de
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Phone sx={{ color: 'primary.main', mt: 0.5 }} />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}
                                        >
                                            Telefon
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            +49-30-959998170
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}
                                        >
                                            Adresse
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            Kronenstr. 18
                                            <br />
                                            10117 Berlin
                                            <br />
                                            Deutschland
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Language sx={{ color: 'primary.main', mt: 0.5 }} />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}
                                        >
                                            Website
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            https://talentillion.de
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Schedule sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Geschäftszeiten
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Montag - Freitag: 09:00 - 18:00 Uhr
                                    <br />
                                    Samstag - Sonntag: Geschlossen
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Content>
    );
}

export default Kontakt;
